import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import FinancialCoreApi from "../../../../../infrastructure/api/financialCore";
import { getApiOrigin } from "../../../../../infrastructure/utils/resolveUploadUrl";
import {
  PAYMENT_METHOD_LEGACY,
  PAYMENT_METHOD_LOAN,
  PAYMENT_METHODS,
  STATUS_WAITING_APPROVAL,
} from "../../../../components/request/case-tabs/financialCoreConstants";
import {
  parseAmountInput,
  parseApiErrorMessage,
} from "../../../../components/request/case-tabs/financialCoreUtils";
import FileUploader from "../../../../components/shared/uploader/FileUploader";

const EMPTY_FORM = {
  amount: "",
  paymentMethod: 2,
  trackingNumber: "",
  description: "",
  receiptFileUrl: "",
};

const MANUAL_PAYMENT_METHODS = PAYMENT_METHODS.filter(
  (m) => m.value !== PAYMENT_METHOD_LEGACY && m.value !== PAYMENT_METHOD_LOAN
);

const formatRial = (value) =>
  value != null && Number(value) > 0
    ? `${Number(value).toLocaleString("fa-IR")} ریال`
    : "—";

const RegisterContractPaymentDialog = ({ row, onClose, onRegistered }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (row) setForm(EMPTY_FORM);
  }, [row]);

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const handleSubmit = async () => {
    if (!row?.requestId) return;
    const amount = parseAmountInput(form.amount);
    if (!amount || amount <= 0) {
      toast.warning("مبلغ را وارد کنید.");
      return;
    }
    if (!form.receiptFileUrl) {
      toast.warning("فایل رسید پرداخت را بارگذاری کنید.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await FinancialCoreApi.registerManualTransaction("SolarentOperator", {
        requestInfoId: Number(row.requestId),
        amount,
        paymentMethod: Number(form.paymentMethod),
        paymentStatus: STATUS_WAITING_APPROVAL,
        trackingNumber: form.trackingNumber || null,
        receiptFileUrl: form.receiptFileUrl || null,
        description: form.description || null,
      });
      if (res?.isSuccess === false) {
        toast.error(parseApiErrorMessage(res, "ثبت تراکنش ناموفق بود."));
        return;
      }
      const txData = res?.data ?? res?.Data;
      const duplicateWarning = txData?.duplicateWarning ?? txData?.DuplicateWarning;
      if (duplicateWarning) toast.warning(duplicateWarning);
      toast.success(res?.message || "تراکنش ثبت شد و در انتظار تایید است");
      onRegistered();
    } catch {
      toast.error("ثبت تراکنش ناموفق بود.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={Boolean(row)} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>ثبت تراکنش پرداخت</DialogTitle>
      <DialogContent>
        {row ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">متقاضی: {row.applicantName || "—"}</Typography>
            <Typography variant="body2">کد پروژه: {row.projectCode || "—"}</Typography>
            <Typography variant="body2">
              شماره قرارداد: {row.contractNumber || "—"}
            </Typography>
            <Typography variant="body2">
              مبلغ کل قرارداد: {formatRial(row.totalCostRial)}
            </Typography>
          </Box>
        ) : null}
        <Stack spacing={1.5}>
          <TextField
            label="مبلغ پرداختی (ریال)"
            size="small"
            fullWidth
            required
            value={form.amount}
            disabled={submitting}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            helperText={
              parseAmountInput(form.amount) > 0
                ? formatRial(parseAmountInput(form.amount))
                : " "
            }
          />
          <FormControl size="small" fullWidth disabled={submitting}>
            <InputLabel id="ac-pay-method">روش پرداخت</InputLabel>
            <Select
              labelId="ac-pay-method"
              label="روش پرداخت"
              value={form.paymentMethod}
              onChange={(e) =>
                setForm((f) => ({ ...f, paymentMethod: e.target.value }))
              }
            >
              {MANUAL_PAYMENT_METHODS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="شماره پیگیری"
            size="small"
            fullWidth
            value={form.trackingNumber}
            disabled={submitting}
            onChange={(e) =>
              setForm((f) => ({ ...f, trackingNumber: e.target.value }))
            }
          />
          <TextField
            label="توضیح"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.description}
            disabled={submitting}
            onChange={(e) =>
              setForm((f) => ({ ...f, description: e.target.value }))
            }
          />
          <FileUploader
            moduleName="SLN"
            label="فایل رسید پرداخت"
            accept="image/*,application/pdf"
            maxFiles={1}
            baseUrl={getApiOrigin()}
            value={form.receiptFileUrl}
            onChange={(url) =>
              setForm((f) => ({
                ...f,
                receiptFileUrl: Array.isArray(url) ? url[0] || "" : url || "",
              }))
            }
          />
          <Typography variant="caption" color="text.secondary">
            تراکنش با وضعیت «در انتظار تایید» ثبت می‌شود و باید از تب مالی پرونده
            تایید شود.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          انصراف
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {submitting ? "در حال ثبت…" : "ثبت تراکنش"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

RegisterContractPaymentDialog.propTypes = {
  row: PropTypes.shape({
    requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    applicantName: PropTypes.string,
    projectCode: PropTypes.string,
    contractNumber: PropTypes.string,
    totalCostRial: PropTypes.number,
  }),
  onClose: PropTypes.func.isRequired,
  onRegistered: PropTypes.func.isRequired,
};

export default RegisterContractPaymentDialog;
